import posthog from 'posthog-js';

type Platform = 'web' | 'mobile';
type Surface = 'fan' | 'creator';
type UserType = 'creator' | 'fan' | 'moderator' | 'anonymous';
type TabType = 'creator_posts' | 'crew_posts' | 'topics';
type Hierarchy = 'post' | 'comment' | 'reply';
type Source =
  | 'feed'
  | 'notification'
  | 'deep_link'
  | 'search'
  | 'direct'
  | 'topic'
  | 'pinned'
  | 'unknown';

interface BaseCommunityProps {
  creator_id?: string;
  user_id?: string;
  user_type?: UserType;
  platform?: Platform;
  surface?: Surface;
  community_id?: string;
}

const send = (event: string, props: Record<string, any>) => {
  posthog.capture(event, {
    ...props,
    platform: props.platform ?? 'web',
    timestamp: Date.now(),
  });
};

// -----------------------------------------------------------------------------
// Feed lifecycle
// -----------------------------------------------------------------------------

export const trackCommunityFeedViewed = (
  data: BaseCommunityProps & {
    tab_type?: TabType;
    sort_type?: string;
    filter_type?: string;
    is_topic?: boolean;
    topic_id?: string;
    source?: Source;
  },
) => send('community_feed_viewed', data);

export const trackCommunityFeedExited = (
  data: BaseCommunityProps & {
    duration_seconds: number;
    tab_type?: TabType;
    sort_type?: string;
    filter_type?: string;
    posts_clicked_count?: number;
    max_scroll_depth?: number;
  },
) => send('community_feed_exited', data);

// -----------------------------------------------------------------------------
// Post detail lifecycle (entry is already captured via existing `post_viewed`)
// -----------------------------------------------------------------------------

export const trackCommunityPostExited = (
  data: BaseCommunityProps & {
    post_id: string;
    duration_seconds: number;
    voted?: boolean;
    commented?: boolean;
    scrolled_to_comments?: boolean;
    content_type?: string;
  },
) => send('community_post_exited', data);

// -----------------------------------------------------------------------------
// Topic lifecycle
// -----------------------------------------------------------------------------

export const trackCommunityTopicViewed = (
  data: BaseCommunityProps & {
    topic_id: string;
    topic_name?: string;
    source?: Source;
  },
) => send('community_topic_viewed', data);

export const trackCommunityTopicExited = (
  data: BaseCommunityProps & {
    topic_id: string;
    duration_seconds: number;
  },
) => send('community_topic_exited', data);

// -----------------------------------------------------------------------------
// Feed interactions
// -----------------------------------------------------------------------------

export const trackCommunityTabChanged = (
  data: BaseCommunityProps & {
    from_tab?: TabType;
    to_tab: TabType;
    tab_index: number;
  },
) => send('community_tab_changed', data);

export const trackCommunityFilterApplied = (
  data: BaseCommunityProps & {
    filter_type: string;
    tab_type?: TabType;
  },
) => send('community_filter_applied', data);

export const trackCommunitySortApplied = (
  data: BaseCommunityProps & {
    sort_type: string;
    tab_type?: TabType;
  },
) => send('community_sort_applied', data);

export const trackCommunitySearchExecuted = (
  data: BaseCommunityProps & {
    query_length: number;
    has_results?: boolean;
    result_count?: number;
    search_scope?: 'community' | 'topic';
  },
) => send('community_search_executed', data);

export const trackCommunitySearchResultClicked = (
  data: BaseCommunityProps & {
    result_post_id: string;
    result_position: number;
    query_length: number;
  },
) => send('community_search_result_clicked', data);

export const trackCommunityFeedRefreshed = (
  data: BaseCommunityProps & {
    tab_type?: TabType;
  },
) => send('community_feed_refreshed', data);

export const trackCommunityFeedPaginated = (
  data: BaseCommunityProps & {
    page_number: number;
    tab_type?: TabType;
  },
) => send('community_feed_paginated', data);

// -----------------------------------------------------------------------------
// Post actions
// -----------------------------------------------------------------------------

export const trackCommunityPostShared = (
  data: BaseCommunityProps & {
    post_id: string;
    share_method?: 'native' | 'copy_link' | 'modal' | 'unknown';
  },
) => send('community_post_shared', data);

export const trackCommunityPostReported = (
  data: BaseCommunityProps & {
    post_id: string;
    hierarchy_type?: Hierarchy;
  },
) => send('community_post_reported', data);

export const trackCommunityPostEditStarted = (
  data: BaseCommunityProps & {
    post_id: string;
    hierarchy_type?: Hierarchy;
  },
) => send('community_post_edit_started', data);

export const trackCommunityPostDeleted = (
  data: BaseCommunityProps & {
    post_id: string;
    hierarchy_type?: Hierarchy;
    is_moderator_action?: boolean;
  },
) => send('community_post_deleted', data);

export const trackCommunityPostPinned = (
  data: BaseCommunityProps & {
    post_id: string;
    action: 'pin' | 'unpin';
  },
) => send('community_post_pinned', data);

export const trackCommunityCommentPinned = (
  data: BaseCommunityProps & {
    comment_id: string;
    post_id: string;
    creator_id: string;
    action: 'pin' | 'unpin';
    is_moderator_action: boolean;
  },
) => send('community_comment_pinned', data);

export const trackCommunityPostAnalyticsViewed = (
  data: BaseCommunityProps & {
    post_id: string;
  },
) => send('community_post_analytics_viewed', data);

// -----------------------------------------------------------------------------
// Post creation flow
// -----------------------------------------------------------------------------

export const trackCommunityPostComposerOpened = (
  data: BaseCommunityProps & {
    composer_type: 'fan_post' | 'creator_post' | 'comment' | 'reply';
    parent_post_id?: string;
  },
) => send('community_post_composer_opened', data);

export const trackCommunityPostComposerClosed = (
  data: BaseCommunityProps & {
    composer_type: 'fan_post' | 'creator_post' | 'comment' | 'reply';
    submitted: boolean;
  },
) => send('community_post_composer_closed', data);

export const trackCommunityPostPublished = (
  data: BaseCommunityProps & {
    post_id: string;
    author_type: 'fan' | 'creator';
    has_media?: boolean;
    has_poll?: boolean;
    is_draft?: boolean;
    is_topic_post?: boolean;
  },
) => send('community_post_published', data);

export const trackCommunityPostPublishFailed = (
  data: BaseCommunityProps & {
    author_type: 'fan' | 'creator';
    error_reason?: string;
  },
) => send('community_post_publish_failed', data);

// -----------------------------------------------------------------------------
// Comments
// -----------------------------------------------------------------------------

export const trackCommunityCommentSubmitted = (
  data: BaseCommunityProps & {
    post_id: string;
    parent_type: Hierarchy;
    has_media?: boolean;
    has_poll?: boolean;
    has_mentions?: boolean;
  },
) => send('community_comment_submitted', data);
